"use client";

import { apiGetLiveTopicIds } from "@/api/event";
import { apiGetTopicTree } from "@/api/header";
import { localFirstCacheKeys, readLocalFirstQueryCache, writeLocalFirstQueryCache } from "@/lib/query-local-first";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface TopicItem {
    id: number; // 分类 id
    name: string; // 分类名称
    typeLevel: number; // 分类级别
    pictureUrl: string; // 图片 url
    parentId: number; // 父分类 id
    parent: null | TopicItem; // 对父分类的引用，后处理得到
    children: TopicItem[]; // 子分类列表
    sort?: number; // 排序字段
    ifSport?: boolean; // 是否为运动类标签页
}

interface TopicState {
    // 缓存的 topics 数据
    normalTopics: TopicItem[]; // 通用页面的 topics
    liveTopics: TopicItem[]; // Live 页面的 topics
    sportsIds: number[];

    // 终端菜单选中状态
    selectedNormalTopic: TopicItem | null;
    selectedLiveTopic: TopicItem | null;

    // 持久化的选中状态 id（用于恢复）
    selectedNormalTopicId: number | null;
    selectedLiveTopicId: number | null;

    normalTopicChains: TopicItem[];
    liveTopicChains: TopicItem[];
    firstTopicChains: TopicItem[];

    // 运动分类的周筛选
    sportTimeRange: "past1w" | "past2w" | "past3w" | "upcoming" | "upcoming2w" | "upcoming3w";
    setSportTimeRange: ( range: "past1w" | "past2w" | "past3w" | "upcoming" | "upcoming2w" | "upcoming3w" ) => void;

    fetchNormalTopics: () => void;
    fetchLiveTopics: () => void;

    // 设置通用 topics
    setNormalTopics: ( topics: TopicItem[] ) => void;

    updateLiveTopicChains: () => void;
    updateNormalTopicChains: () => void;

    // 设置 Live topics
    setLiveTopics: ( topics: TopicItem[] ) => void;

    // 设置通用页面一级菜单选中状态
    setSelectedNormalTopic: ( id: TopicItem ) => void;

    // 设置 Live 页面一级菜单选中状态
    setSelectedLiveTopic: ( id: TopicItem ) => void;
}

function walkTopics( topics: TopicItem[], parent: TopicItem | null = null ) {
    // 递归 topic，为每个项添加向上的引用

    topics.forEach( item => {
        item.parent = parent;
        item.children && walkTopics( item.children, item );
    } );
}

// 根据 id 在 topics 树中查找 topic（供 router 等调用）
export function findTopicById( topics: TopicItem[], id: number ): TopicItem | null {
    for ( const topic of topics ) {
        if ( topic.id === id ) {
            return topic;
        }
        if ( topic.children && topic.children.length > 0 ) {
            const found = findTopicById( topic.children, id );
            if ( found ) {
                return found;
            }
        }
    }
    return null;
}

const TOPIC_TREE_STORAGE_KEY = localFirstCacheKeys.public.topicTree();

export const useTopicStore = create<TopicState>()(
    devtools(
        ( set, get ) => ( {
            normalTopics: [],
            liveTopics: [],
            selectedNormalTopic: null,
            selectedLiveTopic: null,
            selectedNormalTopicId: null,
            selectedLiveTopicId: null,
            normalTopicChains: [],
            liveTopicChains: [],
            sportsIds: [],
            firstTopicChains: [], // 只有 normal 分类的时候才有用
            sportTimeRange: "upcoming", // 默认 Week1
            setSportTimeRange: ( range ) => set( { sportTimeRange: range } ),
            updateNormalTopicChains() { // 目前选中的 normal topic 链路
                const state = get();
                if ( state.selectedNormalTopic === null ) {
                    return [];
                }
                const tc: TopicItem[] = [];
                let tempTopic: TopicItem | null = state.selectedNormalTopic;
                while ( tempTopic ) {
                    tc.unshift( tempTopic );
                    tempTopic = tempTopic.parent;
                }
                set( { normalTopicChains: tc } );
            },
            updateLiveTopicChains() { // 目前选中的 live stopic 链路
                const state = get();
                if ( state.selectedLiveTopic === null ) {
                    return [];
                }
                const tc: TopicItem[] = [];
                let tempTopic: TopicItem | null = state.selectedLiveTopic;
                while ( tempTopic ) {
                    tc.unshift( tempTopic );
                    tempTopic = tempTopic.parent;
                }
                set( { liveTopicChains: tc } );
            },
            fetchNormalTopics: async () => {
                const state = get();
                const applyNormalTopicsPayload = ( topics: TopicItem[] ) => {
                    state.setNormalTopics( topics );

                    const ids = [] as number[];
                    const walkIds = ( items: TopicItem[] ) => {
                        items.forEach( i => {
                            ids.push( i.id );
                            if ( Array.isArray( i.children ) ) {
                                walkIds( i.children );
                            }
                        } );
                    };
                    topics.forEach( item => {
                        if ( item.name === "Sports" ) {
                            ids.push( item.id );
                            Array.isArray( item.children ) && walkIds( item.children );
                        }
                    } );
                    const firstTopicChains = [] as TopicItem[];
                    let temp = topics[0];
                    while ( temp ) {
                        firstTopicChains.push( temp );
                        temp = temp?.children?.[0];
                    }

                    set( {
                        firstTopicChains,
                        sportsIds: ids,
                    } );
                };

                const cachedTopicTree = readLocalFirstQueryCache<TopicItem[]>( TOPIC_TREE_STORAGE_KEY );
                if ( cachedTopicTree?.data?.length ) {
                    applyNormalTopicsPayload( cachedTopicTree.data );
                }

                const [ is_ok, res ] = await apiGetTopicTree();
                if ( is_ok ) {
                    writeLocalFirstQueryCache( TOPIC_TREE_STORAGE_KEY, res.data );
                    applyNormalTopicsPayload( res.data );
                }
            },
            fetchLiveTopics: async () => {
                const state = get();
                const [ is_ok, res ] = await apiGetLiveTopicIds();
                if ( is_ok ) {
                    state.setLiveTopics( res.data );
                }
            },
            setNormalTopics: ( topics ) => {
                const state = get();
                walkTopics( topics );

                // 优先恢复选中状态 (现在仅在内存中有效)
                if ( state.selectedNormalTopicId !== null ) {
                    const restoredTopic = findTopicById( topics, state.selectedNormalTopicId );
                    if ( restoredTopic ) {
                        set( {
                            normalTopics: topics,
                            selectedNormalTopic: restoredTopic,
                        } );
                        state.updateNormalTopicChains();
                        return;
                    }
                    set( { selectedNormalTopicId: null } );
                }

                // 如果没有选中状态且有数据，初始化为第一个
                if ( state.selectedNormalTopic === null && topics.length > 0 ) {
                    let target: TopicItem | null = null;
                    let innerTopics: TopicItem[] = topics;
                    while ( innerTopics && innerTopics.length ) {
                        target = innerTopics[0];
                        innerTopics = innerTopics[0].children;
                    }
                    set( {
                        normalTopics: topics,
                        selectedNormalTopic: target,
                        selectedNormalTopicId: target?.id ?? null,
                    } );
                } else {
                    set( { normalTopics: topics } );
                }
                state.updateNormalTopicChains();
            },

            setLiveTopics: ( topics ) => {
                const state = get();
                walkTopics( topics );

                // 优先恢复选中状态
                if ( state.selectedLiveTopicId !== null ) {
                    const restoredTopic = findTopicById( topics, state.selectedLiveTopicId );
                    if ( restoredTopic ) {
                        set( {
                            liveTopics: topics,
                            selectedLiveTopic: restoredTopic,
                        } );
                        state.updateLiveTopicChains();
                        return;
                    }
                    set( { selectedLiveTopicId: null } );
                }

                // 如果没有选中状态且有数据，初始化为第一个
                if ( state.selectedLiveTopic === null && topics.length > 0 ) {
                    let target: TopicItem | null = null;
                    let innerTopics: TopicItem[] = topics;
                    while ( innerTopics && innerTopics.length ) {
                        target = innerTopics[0];
                        innerTopics = innerTopics[0].children;
                    }
                    set( {
                        liveTopics: topics,
                        selectedLiveTopic: target,
                        selectedLiveTopicId: target?.id ?? null,
                    } );
                } else {
                    set( { liveTopics: topics } );
                }
                state.updateLiveTopicChains();
            },

            setSelectedNormalTopic: ( selected ) => {
                const state = get();
                set( {
                    selectedNormalTopic: selected,
                    selectedNormalTopicId: selected?.id ?? null,
                } );
                state.updateNormalTopicChains();
            },

            setSelectedLiveTopic: ( selected ) => {
                const state = get();
                set( {
                    selectedLiveTopic: selected,
                    selectedLiveTopicId: selected?.id ?? null,
                } );
                state.updateLiveTopicChains();
            },
        } ),
        {
            name: "Topic",
            enabled: process.env.NODE_ENV === "development",
        },
    ),
);
